import { useCallback, useEffect, useState, useMemo } from 'react';
import { Input, Row, Col } from 'antd';
import { Icon } from '@fluentui/react/lib/Icon';

import Layout from '../../../components/Layout/Layout';
import Breadcrumb from '../../../components/Breadcrumb/Breadcrumb';
import PageSubtitle from '../../../components/PageSubtitle/PageSubtitle';
import Button from '../../../components/Button/Button';
import PrefixEditor from './PrefixEditor';
import StatusValue from '../components/StatusValue';
import StyledTable from '../components/StyledTable/StyledTable';

import useLocale from '../../../hooks/useLocale';
import useModal from '../../../hooks/useModal';
import useDebounce from '../../../hooks/useDebounce';
import { ILocale } from '../../../types/ILocale';

import { argumentifyPrefixSearchCriteria } from './utils';
import { getSortableFilterableColumns } from '../utils';
import { IPrefixSearchCriteria } from './types';
import { MAX_PAGE_SIZE } from '../../../constants/common';
import { ClientService } from '../../../shared/api/ClientService';
import API from '../../../utils/api';

import './DocumentPrefix.scss';

function DocumentPrefix(): JSX.Element {
  const { t, locale } = useLocale();
  const { Search } = Input;
  const { showModal, closeModal } = useModal();

  const [searchTerm, setSearchTerm] = useState<string>('');
  const value = useDebounce(searchTerm);

  const [dataSource, setDataSource] = useState<ClientService.DocumentPrefixDto[]>();

  const [totalCount, setTotalCount] = useState<number>();
  const [loading, setLoading] = useState<boolean>(false);
  const [criteria, setCriteria] = useState<IPrefixSearchCriteria>({
    maxResultCount: MAX_PAGE_SIZE,
  });

  const requestPrefixes = useCallback(async () => {
    setLoading(true);
    const response = await API.getDocumentPrefixList(...argumentifyPrefixSearchCriteria(criteria)).catch(() =>
      setLoading(false)
    );
    setLoading(false);

    if (response) {
      setDataSource(response?.items);
      setTotalCount(response?.totalCount || 0);
    }
  }, [criteria]);

  const handleShowEditor = useCallback(
    (documentPrefixId?: string) => {
      showModal(
        <PrefixEditor
          documentPrefixId={documentPrefixId}
          onOk={() => {
            closeModal();
            requestPrefixes();
          }}
          onCancel={closeModal}
        />
      );
    },
    [closeModal, requestPrefixes, showModal]
  );

  useEffect(() => {
    requestPrefixes();
  }, [criteria, requestPrefixes]);

  useEffect(() => {
    setCriteria((prev) => ({ ...prev, filterText: value }));
  }, [value]);

  const columns = useMemo(() => {
    const sourceColumns = [
      {
        title: t.FOLDER_NAME,
        dataIndex: 'documentFolderName',
        key: 'documentFolderName',
        sorter: true,
        filterByName: true,
      },
      {
        title: t.PRE_FIX,
        dataIndex: 'documentPrefixName',
        key: 'documentPrefixName',
        sorter: true,
        filterByName: true,
        render: (documentPrefixName: string, record?: ClientService.DocumentPrefixDto) => (
          <>{locale === ILocale.fr ? record?.frenchName : documentPrefixName || ''}</>
        ),
      },
      {
        title: t.TYPE,
        dataIndex: 'isCustom',
        key: 'isCustom',
        sorter: true,
        render: (isCustom?: boolean) => <>{isCustom ? 'Custom' : 'Standard'}</>,
        filters: [
          {
            text: t.CUSTOM,
            value: true,
          },
          {
            text: t.STANDARD,
            value: false,
          },
        ],
        onFilter: (value: any, record: ClientService.DocumentPrefixDto) => value === record.isCustom,
      },
      {
        title: t.STATUS,
        dataIndex: 'isActive',
        key: 'isActive',
        sorter: true,
        render: (isActive?: boolean) => <StatusValue isActive={isActive} />,
        filters: [
          {
            text: t.ACTIVE,
            value: true,
          },
          {
            text: t.INACTIVE,
            value: false,
          },
        ],
        onFilter: (value: any, record: ClientService.DocumentPrefixDto) => value === record.isActive,
      },
      {
        dataIndex: 'action',
        key: 'action',
        render: (_: any, record: ClientService.DocumentPrefixDto) => (
          <Icon iconName="Edit" onClick={() => handleShowEditor(record?.id)} className="UserManagement__edit-icon" />
        ),
      },
    ];

    return getSortableFilterableColumns(sourceColumns, dataSource);
  }, [
    dataSource,
    handleShowEditor,
    locale,
    t.ACTIVE,
    t.CUSTOM,
    t.FOLDER_NAME,
    t.INACTIVE,
    t.PRE_FIX,
    t.STANDARD,
    t.STATUS,
    t.TYPE,
  ]);

  return (
    <Layout className="DocumentPrefix">
      <Breadcrumb data={[{ title: t.ADMINISTRATION }, { title: t.DOCUMENT_PREFIX }]} />
      <PageSubtitle title={t.DOCUMENT_PREFIX} />
      <Row align="bottom" justify="space-between">
        <Col span={8}>
          <Search
            placeholder={t.SEARCH_BY_FOLDER_NAME_AND_PREFIX}
            allowClear
            size="large"
            onSearch={setSearchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Col>
        <Col>
          <Button onClick={() => handleShowEditor()}>{t.ADD_PRFX}</Button>
        </Col>
      </Row>
      <StyledTable rowKey="id" dataSource={dataSource} columns={columns} total={totalCount} loading={loading} />
    </Layout>
  );
}

export default DocumentPrefix;
